import { Suspense, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { ChevronDown, LogOut, Menu, RefreshCw, Search, UserRound } from "lucide-react";
import { toast } from "sonner";
import { BrandMark } from "@/components/crm/blocks";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { useCrm } from "@/hooks/useCrm";
import { useWorkspace } from "@/hooks/useWorkspace";
import { fmtTime } from "@/lib/dates";
import { boardNavigation, NAVIGATION } from "@/lib/navigation";
import { roleLabel } from "@/lib/schema";
import { TABLE_KEYS } from "@/lib/types";
import { cn } from "@/lib/utils";

// De vaste schermopbouw (vroeger index.html): donkere bovenbalk, zijbalk met navigatie en tellers,
// en het scherm in het midden. Op een smal scherm klapt de zijbalk weg achter de menuknop.

const topButton = "border-white/10 bg-white/[.075] text-white/85 shadow-none hover:bg-white/[.14] hover:text-white hover:border-white/10";

export function AppShell() {
  const crm = useCrm();
  const navigate = useNavigate();
  const { openSearch } = useWorkspace();
  const [menuOpen, setMenuOpen] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const total = TABLE_KEYS.reduce((sum, key) => sum + crm.all(key).length, 0);
  const blocked = Boolean(crm.me?.mustSetup2fa);
  // De eigen borden (fase 9) komen meteen na "Werken".
  const boards = boardNavigation(crm);
  const groups = boards ? [NAVIGATION[0]!, boards, ...NAVIGATION.slice(1)] : NAVIGATION;

  const refresh = async () => {
    setRefreshing(true);
    await crm.reload();
    setRefreshing(false);
    toast.success("Gegevens vernieuwd", { description: "Opnieuw geladen van de server." });
  };

  return (
    <div className="grid h-full grid-rows-[72px_minmax(0,1fr)] overflow-hidden">
      <header className="relative z-30 grid min-w-0 grid-cols-[auto_minmax(0,1fr)] items-center border-b border-white/10 bg-navy-950 text-white md:grid-cols-[252px_minmax(0,1fr)]">
        <div className="flex h-full items-center gap-[11px] border-white/10 px-5 md:border-r">
          <BrandMark className="size-[38px] shrink-0 text-blush" />
          <div className="min-w-0">
            <div className="font-brand text-[17px] font-semibold leading-[1.1] tracking-[.08em] text-blush">FURNIFLATS</div>
            <div className="mt-0.5 text-[10px] uppercase tracking-[.1em] text-white/55">{crm.status?.demo ? "CRM · demo" : "CRM · live"}</div>
          </div>
        </div>
        <div className="flex min-w-0 items-center justify-end gap-2 pr-3.5 md:justify-between md:pl-[22px]">
          {!blocked && (
            <button
              type="button"
              onClick={openSearch}
              className="hidden h-[38px] w-full max-w-[420px] items-center gap-2.5 rounded-[10px] border border-white/10 bg-white/[.075] px-3 text-left text-[13px] text-white/55 hover:bg-white/[.12] md:flex"
            >
              <Search className="size-4" />
              <span className="flex-1 truncate">Zoek pand, persoon, contract, scherm…</span>
              <kbd className="rounded border border-white/15 px-1.5 text-[10px] text-white/45">Ctrl K</kbd>
            </button>
          )}
          <div className="flex items-center gap-2">
            {!blocked && (
              <Button size="icon" className={cn(topButton, "md:hidden")} onClick={openSearch} aria-label="Zoeken">
                <Search />
              </Button>
            )}
            <Button size="icon" className={cn(topButton, "md:hidden")} onClick={() => setMenuOpen((open) => !open)} aria-label="Menu">
              <Menu />
            </Button>
            <span className="inline-flex h-[30px] items-center gap-[7px] whitespace-nowrap rounded-full bg-tone-teal/90 px-[11px] text-[11px] font-bold text-white">
              <span className="size-[7px] rounded-full bg-current" /> Live
            </span>
            {!blocked && (
              <Button size="icon" className={topButton} onClick={refresh} disabled={refreshing} aria-label="Gegevens vernieuwen" title="Gegevens opnieuw laden">
                <RefreshCw className={cn(refreshing && "animate-spin")} />
              </Button>
            )}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button className={cn(topButton, "px-2.5")} aria-label={`Mijn account (${crm.me?.email})`}>
                  <span className="grid size-6 place-items-center rounded-full bg-blush text-[10px] font-extrabold text-navy-950">{initials(crm.me?.name)}</span>
                  <span className="hidden sm:inline">{crm.me?.name}</span>
                  <ChevronDown />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuLabel>
                  <div className="font-bold">{crm.me?.name}</div>
                  <div className="text-[11px] font-normal text-muted-foreground">
                    {crm.me?.email} · {roleLabel(crm.me?.role ?? "")}
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onSelect={() => navigate("/account")}>
                  <UserRound /> Mijn account
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={() => void crm.logout()}>
                  <LogOut /> Afmelden
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      <div className="relative grid min-h-0 min-w-0 overflow-hidden md:grid-cols-[252px_minmax(0,1fr)]">
        <aside
          className={cn(
            "absolute inset-y-0 left-0 z-20 flex w-[252px] min-h-0 flex-col overflow-hidden bg-navy-900 text-white/70 shadow-pop max-md:transition-transform md:static md:translate-x-0 md:shadow-none",
            menuOpen ? "translate-x-0" : "-translate-x-full"
          )}
          aria-label="Hoofdnavigatie"
        >
          <nav className="min-h-0 flex-1 overflow-y-auto px-3 pb-[18px] pt-4">
            {groups.map((group) => {
              const items = group.items.filter((item) => !item.permission || crm.can(item.permission));
              if (!items.length || blocked) return null;
              return (
                <div key={group.label} className="mb-[18px]">
                  <div className="px-[11px] pb-[7px] text-[10px] font-extrabold uppercase tracking-[.12em] text-white/35">{group.label}</div>
                  {items.map((item) => {
                    const count = item.count?.(crm);
                    return (
                      <NavLink
                        key={item.to}
                        to={item.to}
                        end={item.to === "/" || item.end}
                        onClick={() => setMenuOpen(false)}
                        className={({ isActive }) =>
                          cn(
                            "relative flex min-h-[38px] w-full items-center gap-2.5 rounded-[9px] px-2.5 py-2 text-left text-[13px] text-white/70 hover:bg-white/[.06] hover:text-white",
                            isActive && "bg-white/[.11] text-white before:absolute before:-left-3 before:bottom-2 before:top-2 before:w-[3px] before:rounded-r-[3px] before:bg-blush"
                          )
                        }
                      >
                        <item.icon className="size-4 shrink-0" />
                        <span className="min-w-0 flex-1 truncate">{item.label}</span>
                        {count !== undefined && count > 0 && <span className="rounded-full bg-white/[.08] px-[7px] py-px text-[10px] text-white/60">{count}</span>}
                      </NavLink>
                    );
                  })}
                </div>
              );
            })}
          </nav>
          <div className="shrink-0 border-t border-white/[.07] p-3">
            <div className="rounded-[11px] border border-white/[.06] bg-white/[.055] p-[11px]">
              <div className="flex items-center justify-between gap-2">
                <strong className="text-xs text-white">FurniFlats CRM</strong>
                <span className="rounded-full bg-tone-teal-soft px-2 py-0.5 text-[10px] font-bold text-tone-teal">Live</span>
              </div>
              <p className="mt-[5px] text-[11px] text-white/50">{blocked ? "Stel eerst 2FA in." : `${total} records · bijgewerkt ${crm.fetchedAt ? fmtTime(crm.fetchedAt) : "—"}`}</p>
            </div>
          </div>
        </aside>
        {menuOpen && <button className="absolute inset-0 z-10 bg-navy-950/30 md:hidden" aria-label="Menu sluiten" onClick={() => setMenuOpen(false)} />}

        <main className="relative min-h-0 min-w-0 overflow-auto bg-[radial-gradient(circle_at_82%_4%,rgba(241,212,200,.35),transparent_30%)] bg-surface-soft">
          <div className="mx-auto w-full max-w-[1500px] px-[22px] pb-[34px] pt-5">
            {/* De schermen laden apart (code splitting); intussen een rustige plaatshouder. */}
            <Suspense fallback={<p className="py-10 text-center text-xs text-muted-foreground">Laden…</p>}>
              <Outlet />
            </Suspense>
          </div>
        </main>
      </div>
    </div>
  );
}

const initials = (name = "") =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]!.toUpperCase())
    .join("") || "?";
